import { LoadingButton } from '@mui/lab'
import { Button, Grid, Stack, Tooltip, Typography } from '@mui/material'
import { getCabinResource } from 'components/Appointment/util'
import { useAppointmentTypeTranslation } from 'components/Appointment/util/useAppointmentTypeTranslation'
import Lottie from 'lottie-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router'
import { AppointmentType, EventType, Route, RouteParameters, ToastSubType } from '../../../../constants'
import {
  SearchParameters,
  useEventTracking,
  useFeatureFlags,
  useLocale,
  useNotification,
  useSearchParameters,
  useShop,
  useTrackError,
} from '../../../../hooks'
import { useAppointmentsService } from '../../../../hooks/services'
import { useCustomerProfileService } from '../../../../hooks/services/customerProfileService'
import { useReservation, useReservationHookName } from '../../../../hooks/useReservation'
import { useAreFeatureFlagsReady } from '../../../../modules/App/Providers/FeatureFlag'
import { Customer, ReservationConvertRequest } from '../../../../types'
import {
  appendSearchParamsToPathname,
  appointmentToTitle,
  appointmentTypeToDisplayText,
  hydratePathname,
  isPooledSchedulingInScope,
  isSlotUnavailableError,
  logFactory,
  toLongTimeZonedDateText,
  toShortTimeZonedDateText,
} from '../../../../util'
import cancelAnimationData from '../../../Appointment/assets/lottie/cancel.json'
import successAnimationData from '../../../Appointment/assets/lottie/success.json'
import { getSpecialistResource, handleSubmissionFailure } from '../../../Appointment/util'
import { Issues } from '../../../Issues'
import { useResources } from '../../../schedule/Schedule/useResources'
import Countdown from '../../Common/Countdown'
import { CreateNewButton } from '../../Common/CreateNewButton'
import { Loading } from '../../Common/Loading'

const log = logFactory('NewReservationForm')

const isExpired = (expiresAt = new Date()): boolean => {
  return new Date(expiresAt).getTime() - new Date().getTime() <= 0
}

const Success = () => {
  const { locale } = useLocale()
  const { cancelReservation, convertReservation, getShops } = useAppointmentsService()
  const { getCustomerProfile } = useCustomerProfileService()
  const { reservation, isLoading: isLoadingReservation } = useReservation()
  const { data: resources, isLoading: isLoadingResources } = useResources({
    resourceIds: reservation?.resource_ids,
  })
  const navigate = useNavigate()
  const { track: trackEvent } = useEventTracking()
  const { notify } = useNotification()
  const trackError = useTrackError()
  const { t } = useTranslation()
  const searchParameters = useSearchParameters()
  const [isAvailabilityCacheEnabled, isPooledSchedulingEnabled] = useFeatureFlags<boolean>([
    'ENABLE_AVAILABILITY_CACHE',
    'POOLED_SCHEDULING',
  ])
  const areFeatureFlagsReady = useAreFeatureFlagsReady()
  const { shop } = useShop()
  const [isCurrentlyExpired, setIsCurrentlyExpired] = useState(isExpired(reservation?.expires_at))
  const queryClient = useQueryClient()
  const [isCanceling, setIsCanceling] = useState(false)
  const [isScheduling, setIsScheduling] = useState(false)
  const [isCurrentlyErrored, setIsCurrentlyErrored] = useState(false)

  const shopId = resources?.data?.find((r) => r.shop_id)?.shop_id

  const couldBePooled = isPooledSchedulingInScope({
    appointmentType: reservation?.appointment_type,
    country: reservation?.country,
    journey: reservation?.journey,
  })
  const isPooled = couldBePooled && Boolean(isPooledSchedulingEnabled)

  const appointmentParams = {
    appointmentType: appointmentTypeToDisplayText[reservation?.appointment_type ?? AppointmentType.HEARING_AID_EVALUATION],
    fitterName: getSpecialistResource(resources?.data)?.name,
    cabinName: getCabinResource(resources?.data)?.name,
    shopId,
    hideProviderName: isPooled,
  }

  const { getAppointmentTypeTranslation } = useAppointmentTypeTranslation(appointmentParams)

  const customer_gid = searchParameters.customer_gid as string
  const opportunity_gid = searchParameters.opportunity_gid as string

  useEffect(() => {
    const intervalId = setInterval(() => {
      setIsCurrentlyExpired(isExpired(reservation?.expires_at))
    }, 100)

    return () => clearInterval(intervalId)
  }, [reservation])

  if (isLoadingReservation || isLoadingResources || (couldBePooled && !areFeatureFlagsReady)) {
    return <Loading />
  }

  if (!reservation) {
    return <Issues summary={{ 'reservation.error': undefined }} />
  }

  const dateText = toLongTimeZonedDateText(reservation.start_date, reservation.timezone, locale)
  const customerTime = toShortTimeZonedDateText(reservation.start_date, reservation.timezone, locale)

  const shopGid = searchParameters.shop_gid ?? shop?.gid
  const getSearchParams = () => {
    return {
      [SearchParameters.CUSTOMER_GID]: customer_gid,
      [SearchParameters.OPPORTUNITY_GID]: opportunity_gid,
      [SearchParameters.JOURNEY]: reservation.journey,
      ...(shopGid ? { [SearchParameters.SHOP_GID]: shopGid } : {}),
    }
  }

  const onCancel = async () => {
    try {
      setIsCanceling(true)
      log(`📩 Submitting reservation cancel for id ${reservation.id}...`)
      await cancelReservation(reservation.id)
      trackEvent(EventType.RESERVATION_CANCELLATION_SUBMISSION_SUCCEEDED)
      const pathname = hydratePathname(Route.RESERVATION_CANCEL_SUCCESS, {
        [RouteParameters.COUNTRY]: reservation.country,
        [RouteParameters.RESERVATION_ID]: reservation.id,
      })
      const pathnameAndSearch = appendSearchParamsToPathname({
        pathname,
        searchParams: getSearchParams(),
      })
      navigate(pathnameAndSearch)
    } catch (error) {
      setIsCanceling(false)
      const fallbackErrorMessage = 'Sorry, there was an error while canceling the reservation.'
      trackEvent(EventType.RESERVATION_CANCELLATION_SUBMISSION_FAILED)

      handleSubmissionFailure({
        error: error,
        fallbackErrorMessage,
        notify,
        trackError,
        trackEvent,
      })
    }
  }

  const onSchedule = async () => {
    const invalidateQueries = () => queryClient.invalidateQueries([useReservationHookName])
    try {
      setIsScheduling(true)
      log(`📩 Submitting reservation with id ${reservation.id} to convert to appointment...`)
      // Getting customer data
      const customerProfile = await getCustomerProfile({
        anyRelationId: reservation.lead_gid,
      })

      // Notify that the lead has not been converted yet or there was an error in the service
      if (!customer_gid) {
        notify(
          {
            message: 'A customer account must have been created before scheduling the appointment.',
            type: ToastSubType.UNHANDLED_ERROR,
          },
          'warning',
          { persist: true }
        )
        return null
      }

      // To convert a reservation to a schedule, it must be loaded from the Opportunity page with the opportunity parameter
      if (!opportunity_gid) {
        notify(
          {
            message: 'An opportunity must have been created before scheduling the appointment.',
            type: ToastSubType.UNHANDLED_ERROR,
          },
          'warning',
          { persist: true }
        )
        return null
      }

      const customer = {
        givenName: customerProfile.lastName,
        familyName: customerProfile.firstName,
      } as Customer

      // Build request
      const request: ReservationConvertRequest = {
        country: reservation.country,
        customer_salesforce_id: customer_gid,
        is_manual: false,
        journey: reservation.journey,
        opportunity_gid: opportunity_gid,
        title: appointmentToTitle({
          customer,
          type: reservation.appointment_type,
        }),
      }

      await convertReservation(reservation.id, request)
      // Refresh the reservation data
      invalidateQueries()
      trackEvent(EventType.RESERVATION_CONVERSION_SUBMISSION_SUCCEEDED)
      const pathname = hydratePathname(Route.RESERVATION_CONVERT_TO_APPOINTMENT_SUCCESS, {
        [RouteParameters.COUNTRY]: reservation.country,
        [RouteParameters.RESERVATION_ID]: reservation.id,
      })
      navigate(pathname)
    } catch (error) {
      const slotUnavailable = isSlotUnavailableError(error)
      setIsCurrentlyErrored(slotUnavailable)
      setIsScheduling(false)
      const fallbackErrorMessage = 'Sorry, there was an error while scheduling the appointment.'

      if (slotUnavailable) {
        trackEvent(EventType.RESERVATION_CONVERSION_SUBMISSION_FAILED_SLOT_UNAVAILABLE, {
          data: { wasAvailabilityCacheEnabled: isAvailabilityCacheEnabled },
        })
      } else {
        trackEvent(EventType.RESERVATION_CONVERSION_SUBMISSION_FAILED)
      }

      handleSubmissionFailure({
        error: error,
        fallbackErrorMessage,
        invalidateQueries,
        notify,
        trackError,
        trackEvent,
      })
    }
  }

  const onAvailabilitiesRedirect = async () => {
    const reservedResource = getSpecialistResource(resources?.data) ?? getCabinResource(resources?.data)
    const { data: shops } = await getShops({ id: reservedResource?.shop_id })
    const [shop] = shops

    if (!shop?.gid) {
      notify(
        {
          message: 'There are missing parameters to get the availabilities list',
          type: ToastSubType.UNHANDLED_ERROR,
        },
        'error',
        {
          persist: true,
        }
      )
      return null
    }

    try {
      await cancelReservation(reservation.id)
    } catch {
      // If the cancel fails, we still want to redirect the user to the availabilities page (Eg: Reservation is already expired)
      log(`🚨 "onAvailabilitiesRedirect" - Cancel reservation failed for id ${reservation.id}`)
    }

    const pathname = hydratePathname(Route.CONSULTANT_SCHEDULE, {
      [RouteParameters.COUNTRY]: reservation.country,
    })

    navigate(
      appendSearchParamsToPathname({
        pathname,
        searchParams: {
          ...getSearchParams(),
          [SearchParameters.SHOP_GID]: shop.gid,
        },
      })
    )
  }

  const isReservationFlow = !customer_gid || !opportunity_gid
  const disabledScheduleButtonTitle = isReservationFlow
    ? 'A customer account and opportunity must be created before scheduling an appointment'
    : ''

  const ReservationExpired = (
    <Grid container textAlign={'center'} spacing={1}>
      <Grid item xs={12}>
        <Lottie animationData={cancelAnimationData} loop={false} style={{ height: '12rem' }} />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h1" p={1}>
          This reservation is expired
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Stack direction={'row'} spacing={2} justifyContent={'center'} alignItems={'center'}>
          <CreateNewButton reservation={reservation} />
        </Stack>
      </Grid>
    </Grid>
  )

  const ReservationConfirmed = (
    <Grid container textAlign={'center'} spacing={1}>
      <Grid item xs={12}>
        <Lottie animationData={successAnimationData} loop={false} style={{ height: '12rem' }} />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h1" p={1}>
          {t('pages.reservation.success.message')}
        </Typography>
        <Typography variant="body1">{getAppointmentTypeTranslation()}</Typography>
        <Typography variant="body1">{dateText}</Typography>
        <Typography variant="body1">
          {customerTime} ({t('common.label.customerTime')})
        </Typography>
        <Typography variant="body1">
          {t('pages.reservation.success.expiresIn')} <Countdown targetDate={reservation.expires_at} />
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Stack direction={'row'} spacing={2} justifyContent={'center'} alignItems={'center'}>
          <LoadingButton disabled={isScheduling} loading={isCanceling} onClick={onCancel} variant="outlined">
            {t('pages.reservation.success.actions.cancel')}
          </LoadingButton>
          <Tooltip title={disabledScheduleButtonTitle}>
            <span>
              <LoadingButton disabled={isReservationFlow} loading={isScheduling} onClick={onSchedule} variant="contained">
                {t('pages.reservation.success.actions.schedule')}
              </LoadingButton>
            </span>
          </Tooltip>
        </Stack>
      </Grid>
    </Grid>
  )

  const ReservationUnavailable = (
    <Grid container textAlign={'center'} spacing={1}>
      <Grid item xs={12}>
        <Lottie animationData={cancelAnimationData} loop={false} style={{ height: '12rem' }} />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h1" p={1}>
          The reservation slot is no longer available
        </Typography>
        <Typography variant="h3" mb={3}>
          Please select a different date/time
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Stack direction={'row'} spacing={2} justifyContent={'center'} alignItems={'center'}>
          <LoadingButton disabled={isScheduling} loading={isCanceling} onClick={onCancel} variant="outlined">
            Cancel
          </LoadingButton>
          <Button onClick={onAvailabilitiesRedirect} variant="contained">
            See availabilities
          </Button>
        </Stack>
      </Grid>
    </Grid>
  )

  return isCurrentlyExpired ? ReservationExpired : isCurrentlyErrored ? ReservationUnavailable : ReservationConfirmed
}

export { Success as ReservationSuccess }
